import { useEffect } from "react";
import { useSessionStore } from "../store/useSessionStore";
import { useCashDepositStore } from "../store/useCashDepositStore";
import { RequestStatus } from "../utils/commons";
import {
  getCashDepositContext,
  getFundingPolicy,
  getOperation,
} from "../services/api";

/** Shared in-flight request so every subscriber dedupes to a single fetch. */
let contextFetchPromise: Promise<void> | null = null;
let contextFetchLinkId: string | null = null;

/** Clears the dedupe state so the next claimed link refetches its own context. */
export function resetCashDepositContextFetch(): void {
  contextFetchPromise = null;
  contextFetchLinkId = null;
}

function fetchCashDepositContextOnce(linkId: string): void {
  // Ignore stale calls: only fetch for the link the session is actually
  // claimed for (guards against an old render firing during a link switch).
  const liveLinkId = useSessionStore.getState().session.data?.linkId;
  if (liveLinkId !== linkId) return;

  const { context } = useCashDepositStore.getState();
  if (context) return;

  if (contextFetchPromise && contextFetchLinkId === linkId) return;

  contextFetchLinkId = linkId;
  const { setContext, setStatus } = useCashDepositStore.getState();
  setStatus(RequestStatus.LOADING);

  contextFetchPromise = getCashDepositContext()
    .then(async (data) => {
      const currentLinkId = useSessionStore.getState().session.data?.linkId;
      if (currentLinkId !== linkId) return;
      if (data.requireCustomerToChooseChain === undefined) {
        try {
          const policy = await getFundingPolicy();
          data = { ...data, ...policy };
        } catch {
          // Context still usable; branching treats a missing flag as skip-picker.
        }
      }
      // After splash we need to know if this link can still mint a barcode.
      // Prefer context.fundingStatus; fall back to GET operation for older APIs.
      if (data.operationId && !data.fundingStatus) {
        try {
          const operation = await getOperation(data.operationId);
          data = { ...data, fundingStatus: operation.fundingStatus };
        } catch {
          // Leave fundingStatus unset; landing still works, barcode mint will fail.
        }
      }
      setContext(data);
    })
    .catch(() => {
      setStatus(RequestStatus.ERROR);
    })
    .finally(() => {
      contextFetchPromise = null;
      contextFetchLinkId = null;
    });
}

/** Subscribe to the cash-deposit context (read-only). Safe to call from any page. */
export function useCashDepositContext() {
  const context = useCashDepositStore((state) => state.context);
  const status = useCashDepositStore((state) => state.status);
  return { context, status };
}

/**
 * Ensures the cash-deposit context is fetched once per claimed link. Mount
 * exactly once at the app root — pages should only use `useCashDepositContext`.
 */
export function useEnsureCashDepositContext() {
  const linkId = useSessionStore((state) => state.session.data?.linkId);

  useEffect(() => {
    if (!linkId) return;
    fetchCashDepositContextOnce(linkId);
  }, [linkId]);
}
